import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import { parseDate } from '../lib/dates';
import {
  METRIC_FIELDS_BY_CATEGORY, computeRates, fmtMetricValue,
} from '../lib/metrics';
import './partnership-font.css';

/* ---------- LampTwist editorial tokens (mirror of the partnership page) ---------- */
const LT = {
  paper: '#fbfbfa',
  bone: '#edece5',
  boneDark: '#dedccf',
  ink: '#2b4138',
  ink700: '#3d5448',
  ink500: '#6d8276',
  ink300: '#a8b6ad',
  yellow: '#ffbd09',
  border: 'rgba(43,65,56,0.12)',
  borderStrong: 'rgba(43,65,56,0.2)',
  rCard: 14,
};
const FONT =
  '"TWK Lausanne", "Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const rangeLabel = (a: string | null, b: string | null): string => {
  if (!a || !b) return '';
  const sa = parseDate(a);
  const sb = parseDate(b);
  const sameMonth = sa.getMonth() === sb.getMonth() && sa.getFullYear() === sb.getFullYear();
  if (sameMonth) return `${MONTHS_LONG[sa.getMonth()]} ${sa.getDate()}–${sb.getDate()}, ${sa.getFullYear()}`;
  return `${MONTHS[sa.getMonth()]} ${sa.getDate()} – ${MONTHS[sb.getMonth()]} ${sb.getDate()}, ${sb.getFullYear()}`;
};
const fmtShortDate = (s: string | null): string => {
  if (!s) return '';
  const d = parseDate(s);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
};

const WRAP: React.CSSProperties = { maxWidth: 1180, margin: '0 auto', width: '100%' };

type ResultsAsset = {
  id: number;
  title: string;
  category: 'Email' | 'Social Media' | 'Homepage' | 'Catalog' | 'Promotion';
  asset_type: string;
  publish_date: string | null;
  end_date: string | null;
  proof_url: string;
  proof_screenshot: string;
  proof_extra_screenshots?: string[];
  metrics: Record<string, number | string | null>;
  description: string;
};
type ResultsPayload = {
  campaign: { id: string; name: string; start_date: string | null; end_date: string | null };
  brand: { id: string; name: string; logo: string; color: string; country: string };
  partnership: { slug: string; eyebrow: string };
  assets: ResultsAsset[];
};

const SECTION_TITLES: Record<ResultsAsset['category'], string> = {
  Email: 'Newsletters',
  'Social Media': 'Social',
  Homepage: 'Homepage',
  Catalog: 'Catalog',
  Promotion: 'Promotion',
};
const SECTION_ORDER: ResultsAsset['category'][] = ['Email', 'Social Media', 'Homepage', 'Catalog', 'Promotion'];

const fmtN = (n: number): string => n.toLocaleString('en');

const numericMetric = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

/** Sum a single metric key across every asset in a given category. */
const sumMetric = (
  assets: ResultsAsset[],
  category: ResultsAsset['category'],
  key: string,
): number => {
  let total = 0;
  for (const a of assets) {
    if (a.category !== category) continue;
    const n = numericMetric(a.metrics?.[key]);
    if (n != null) total += n;
  }
  return total;
};

const BrandResults = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [status, setStatus] = useState<'loading' | 'notfound' | 'ready' | 'error'>('loading');
  const [data, setData] = useState<ResultsPayload | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    if (!token) { setStatus('notfound'); return; }
    setStatus('loading');
    axios.get(`api/public/partnerships/${slug}/results/?token=${encodeURIComponent(token)}`)
      .then((res) => { setData(res.data); setStatus('ready'); })
      .catch((err) => {
        const code = (err as { response?: { status?: number } })?.response?.status;
        if (code === 404) setStatus('notfound');
        else setStatus('error');
      });
  }, [slug, token]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const shell = (msg: string, color = LT.ink500) => (
    <div style={{ fontFamily: FONT, background: LT.bone, color: LT.ink, minHeight: '100vh', padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 14, color, maxWidth: '40ch', margin: '0 auto' }}>{msg}</div>
    </div>
  );

  if (status === 'loading') return shell('Loading…');
  if (status === 'notfound') return shell('This results link is not valid (or has been revoked). Please ask your LampTwist contact for a fresh link.', '#b54338');
  if (status === 'error' || !data) return shell('Could not load the results right now. Please try again later.', '#b54338');

  const sections = SECTION_ORDER
    .map((c) => ({
      category: c,
      items: data.assets.filter((a) => a.category === c),
    }))
    .filter((s) => s.items.length > 0);

  // Plain compute — no useMemo here because we're already past the
  // status-guard returns above and React's Rules of Hooks won't let us
  // call hooks conditionally. The data set is small enough that
  // recomputing per render is fine.
  const headlineTotals = {
    newsletterReach: sumMetric(data.assets, 'Email', 'recipients'),
    newsletterOpens: sumMetric(data.assets, 'Email', 'opens'),
    igReach: sumMetric(data.assets, 'Social Media', 'reach'),
    igEngagement: sumMetric(data.assets, 'Social Media', 'engagement'),
  };

  return (
    <div style={{ background: LT.bone, color: LT.ink, fontFamily: FONT, minHeight: '100vh' }}>
      {/* HEADER — mirrors the partnership opt-in page header */}
      <div style={{ background: LT.ink, color: LT.paper, position: 'sticky', top: 0, zIndex: 30 }}>
        <div style={{ ...WRAP, display: 'flex', alignItems: 'center', gap: 24, justifyContent: 'space-between', padding: '18px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
            <img
              src="/lamptwist-wordmark.svg"
              alt="LampTwist"
              style={{ height: 30, display: 'block', filter: 'brightness(0) invert(1)', flexShrink: 0 }}
            />
            <span style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: LT.ink300, paddingLeft: 18, borderLeft: '1px solid rgba(251,251,250,0.18)' }}>
              Campaign results
            </span>
          </div>
          <div style={{ textAlign: 'right', minWidth: 0 }}>
            <div style={{ fontSize: 22, lineHeight: 1.15, fontWeight: 300, letterSpacing: '-0.01em' }}>
              For <span style={{ color: LT.yellow }}>{data.brand.name}</span>
            </div>
            <div style={{ fontSize: 12, color: LT.ink300, marginTop: 2 }}>
              {data.campaign.name}{data.campaign.start_date && data.campaign.end_date ? ` · ${rangeLabel(data.campaign.start_date, data.campaign.end_date)}` : ''}
            </div>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section style={{ background: LT.paper }}>
        <div style={{ ...WRAP, padding: '96px 32px 80px' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: LT.ink500, marginBottom: 18 }}>
            Recap
          </div>
          <h1 style={{ fontSize: 64, lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 300, margin: 0, maxWidth: 22 + 'ch' }}>
            Here's how <span style={{ color: LT.ink700 }}>{data.campaign.name}</span> performed for <span style={{ color: LT.yellow }}>{data.brand.name}</span>.
          </h1>
          <div style={{ fontSize: 15, color: LT.ink500, marginTop: 20 }}>
            {rangeLabel(data.campaign.start_date, data.campaign.end_date)}
          </div>
        </div>
      </section>

      {/* BY THE NUMBERS */}
      {(headlineTotals.newsletterReach + headlineTotals.newsletterOpens + headlineTotals.igReach + headlineTotals.igEngagement) > 0 && (
        <section style={{ background: LT.bone }}>
          <div style={{ ...WRAP, padding: '80px 32px' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: LT.ink500, marginBottom: 22 }}>
              By the numbers
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 28 }}>
              {headlineTotals.newsletterReach > 0 && (
                <Stat value={fmtN(headlineTotals.newsletterReach)} label="Newsletter recipients" />
              )}
              {headlineTotals.newsletterOpens > 0 && (
                <Stat value={fmtN(headlineTotals.newsletterOpens)} label="Newsletter opens" />
              )}
              {headlineTotals.igReach > 0 && (
                <Stat value={fmtN(headlineTotals.igReach)} label="Social reach" />
              )}
              {headlineTotals.igEngagement > 0 && (
                <Stat value={fmtN(headlineTotals.igEngagement)} label="Social engagement" />
              )}
            </div>
          </div>
        </section>
      )}

      {/* SECTIONS — per category */}
      {sections.map(({ category, items }) => (
        <section key={category} style={{ background: LT.paper }}>
          <div style={{ ...WRAP, padding: '80px 32px' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: LT.yellow, marginBottom: 22, fontWeight: 700 }}>
              {SECTION_TITLES[category]}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: category === 'Email' ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
              {items.map((a) => (
                <AssetTile key={a.id} asset={a} onOpen={setLightbox} />
              ))}
            </div>
          </div>
        </section>
      ))}

      {sections.length === 0 && (
        <section style={{ background: LT.paper }}>
          <div style={{ ...WRAP, padding: '80px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: LT.ink500, maxWidth: '40ch', margin: '0 auto' }}>
              The campaign is still being prepared — once the assets go live and proofs are uploaded, they'll show up here. Your LampTwist contact will let you know.
            </div>
          </div>
        </section>
      )}

      {/* CLOSING */}
      <section style={{ background: LT.ink, color: LT.paper }}>
        <div style={{ ...WRAP, padding: '64px 32px 80px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: LT.yellow, marginBottom: 18, fontWeight: 700 }}>
            Thank you
          </div>
          <h2 style={{ fontSize: 36, lineHeight: 1.1, letterSpacing: '-0.02em', fontWeight: 300, margin: 0, maxWidth: '24ch', marginInline: 'auto' }}>
            Thanks for being part of {data.campaign.name}, {data.brand.name}.
          </h2>
          <p style={{ fontSize: 14, color: LT.ink300, marginTop: 16, maxWidth: '46ch', marginInline: 'auto' }}>
            Questions about the numbers, or curious about what's next? Reply to the LampTwist contact who sent you this link.
          </p>
        </div>
      </section>

      {/* Lightbox for proof screenshots */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
            zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, cursor: 'zoom-out',
          }}
        >
          <img src={lightbox} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      )}
    </div>
  );
};

const Stat = ({ value, label }: { value: string; label: string }) => (
  <div style={{ borderTop: `1px solid ${LT.borderStrong}`, paddingTop: 16 }}>
    <div style={{ fontSize: 44, lineHeight: 1, color: LT.ink, fontWeight: 300, letterSpacing: '-0.02em' }}>
      {value}
    </div>
    <div style={{ fontSize: 12, color: LT.ink500, marginTop: 10, letterSpacing: '0.04em' }}>{label}</div>
  </div>
);

const AssetTile = ({ asset, onOpen }: { asset: ResultsAsset; onOpen: (src: string) => void }) => {
  const fields = METRIC_FIELDS_BY_CATEGORY[asset.category] ?? [];
  const rates = computeRates(asset.category, asset.metrics);
  const dateLine = [
    asset.asset_type,
    asset.publish_date ? fmtShortDate(asset.publish_date) : null,
  ].filter(Boolean).join(' · ');
  return (
    <div style={{ background: LT.paper, border: `1px solid ${LT.border}`, borderRadius: LT.rCard, overflow: 'hidden' }}>
      <div style={{ background: LT.bone, position: 'relative' }}>
        {asset.proof_screenshot ? (
          <img
            src={asset.proof_screenshot}
            alt={asset.title}
            onClick={() => onOpen(asset.proof_screenshot)}
            style={{ width: '100%', display: 'block', maxHeight: asset.category === 'Email' ? 760 : 360, objectFit: 'contain', cursor: 'zoom-in' }}
          />
        ) : asset.proof_url ? (
          <a href={asset.proof_url} target="_blank" rel="noopener noreferrer" style={{
            display: 'block', padding: '64px 24px', textAlign: 'center', color: LT.ink500, fontSize: 13,
          }}>
            Open live → <span style={{ color: LT.ink }}>{asset.proof_url}</span>
          </a>
        ) : null}
      </div>
      {asset.proof_screenshot && (asset.proof_extra_screenshots ?? []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 22px 0' }}>
          {(asset.proof_extra_screenshots ?? []).map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${asset.title} ${i + 2}`}
              onClick={() => onOpen(src)}
              style={{ width: 96, height: 72, objectFit: 'cover', objectPosition: 'top', borderRadius: 6, background: LT.bone, border: `1px solid ${LT.border}`, cursor: 'zoom-in' }}
            />
          ))}
        </div>
      )}
      <div style={{ padding: '18px 22px 22px' }}>
        <div style={{ fontSize: 16, fontWeight: 500 }}>{asset.title}</div>
        <div style={{ fontSize: 12, color: LT.ink500, marginTop: 4 }}>{dateLine}</div>
        {(fields.length > 0 || rates.length > 0) && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${LT.border}`, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            {fields.map((f) => {
              const v = fmtMetricValue(asset.metrics?.[f.key]);
              if (v === '—') return null;
              return (
                <div key={f.key}>
                  <div style={{ fontSize: 15, fontWeight: 500, color: LT.ink }}>{v}</div>
                  <div style={{ fontSize: 10.5, color: LT.ink500, marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{f.label}</div>
                </div>
              );
            })}
            {rates.map((r) => (
              <div key={r.label}>
                <div style={{ fontSize: 15, fontWeight: 500, color: LT.yellow }}>{r.value}</div>
                <div style={{ fontSize: 10.5, color: LT.ink500, marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{r.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandResults;
